/**
 * Helper pur — mapping `error code → i18n key` pour les pages
 * /auth/reset/confirm et /auth/accept-invite.
 *
 * Sources d'erreurs possibles (Chunk 7.C) :
 *  1. parseAuthCallbackUrl :
 *      - "parse_failed", "missing_tokens", "unknown_type"
 *      - codes Supabase tels quels : "access_denied", "otp_expired", ...
 *  2. supabase.auth.setSession() / getUser() : on remappe vers "session_failed"
 *  3. État sentinelle interne : "invalid_link" (type ≠ recovery|invite côté page)
 *
 * On centralise le mapping ici pour :
 *  - éviter le hardcode des clés i18n dans plusieurs pages,
 *  - tester unitairement la table de correspondance,
 *  - garantir un fallback systématique (`errors.session.invalid_link`).
 *
 * IMPORTANT : ce helper renvoie UNE CLÉ i18n. Il ne traduit pas. C'est la
 * page qui appelle `t(key)` — keep it pure / testable / SSR-safe.
 */

/**
 * Codes d'erreur connus, dans l'ordre où on les attend en pratique.
 * Tout code non listé tombe sur le fallback.
 */
const KNOWN_ERROR_CODES = new Set<string>([
  // Erreurs locales du parser
  "parse_failed",
  "missing_tokens",
  "unknown_type",
  // Sentinelle interne
  "invalid_link",
  // Erreur post-setSession / getUser
  "session_failed",
  // Codes propagés par Supabase (lien expiré ou refusé)
  "access_denied",
  "otp_expired",
]);

const FALLBACK_KEY = "errors.session.invalid_link";

/**
 * Mappe un code d'erreur vers la clé i18n correspondante.
 *
 * @param code Code d'erreur (peut être null, undefined, ou inconnu).
 * @returns    Clé i18n. Garantit toujours une clé existante dans les bundles.
 */
export function authErrorToI18nKey(code: string | null | undefined): string {
  if (!code) return FALLBACK_KEY;
  if (KNOWN_ERROR_CODES.has(code)) return `errors.session.${code}`;
  return FALLBACK_KEY;
}

/**
 * Liste exhaustive des clés référencées par le helper. Utilisée par les
 * tests pour vérifier que les bundles FR/EN couvrent bien tout.
 */
export const AUTH_ERROR_I18N_KEYS: readonly string[] = [
  ...[...KNOWN_ERROR_CODES].map((c) => `errors.session.${c}`),
  FALLBACK_KEY,
];
